import { useLocation, useNavigate } from "react-router-dom";
import UserHeader from "./UserHeader";
import MenuMobile from "./MenuMobileBrad";
import { useIsMobile } from "../../utils/useIsMobile";
import { useState } from "react";
import Cookies from "js-cookie";
import { useSupabaseClient, useUser } from "@supabase/auth-helpers-react";
import { toast } from "sonner";
import { getLogoImage } from "../../utils/getLogoImage";
import getThemeColors from "../../utils/getColorTheme";
import { IconMenu2 } from "@tabler/icons-react";
import { useTheme } from "../../contexts/ThemeContext";
import useAnalytics from "../../hooks/useAnalytics";
import { WEB_LOGIN_PATH } from "../../routes/authPaths";

const HeaderPageBrad = () => {
  const [openMenuMobile, setOpenMenuMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const supabaseClient = useSupabaseClient();
  const user = useUser(); // Add user authentication check
  const {addTheme, clearWithTheme} = useTheme();
  const logo = getLogoImage(addTheme);
  const { theme, background } = getThemeColors(addTheme);
  const { trackConfirmationAlertClick } = useAnalytics();

  const themeColor =
    addTheme === "dark"
      ? "#F9F5ED"
      : addTheme === "eclipse"
      ? "#16140E"
      : addTheme === "white"
      ? "#FFFFFF"
      : "#16140E";

  const isMobile = useIsMobile();

  // Check if we're on the chat page
  const isChatPage = location.pathname === "/chat";

  // Default navigation for authenticated users
  const navigationLinks = [
    {
      title: <UserHeader onClick={() => logout()} />,
    },
  ];

  const guestAuthButtonStyle = {
    backgroundColor: "#FFD370",
    color: "#16140E",
    border: "none",
    borderRadius: "20px",
    padding: isMobile ? "6px 12px" : "8px 16px",
    fontSize: isMobile ? 14 : 16,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: isMobile ? "auto" : "40px",
    transition: "all 0.2s ease",
  };

  const renderGuestAuthButtons = () => (
    <>
      <li className="d-flex align-items-center">
        <button
          type="button"
          className="nav-item-brad"
          style={{
            color: theme,
            fontWeight: 600,
            fontSize: isMobile ? 14 : 16,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: isMobile ? "4px 8px" : "6px 2px",
          }}
          onClick={() => navigate(WEB_LOGIN_PATH, { state: { isSingup: false } })}
          aria-label="Log in to your account"
        >
          Log in
        </button>
      </li>
      <li className="d-flex align-items-center">
        <button
          type="button"
          className="btn-main"
          style={guestAuthButtonStyle}
          onClick={() => navigate(WEB_LOGIN_PATH, { state: { isSingup: true } })}
          aria-label="Sign up for an account"
        >
          Sign Up
        </button>
      </li>
    </>
  );

  const logout = async () => {
    setIsLoading(true);
    toast.message("Do you really want to log out?", {
      duration: 10000,
      style: {
        width: 370,
      },
      action: {
        label: "Yes",
        onClick: async () => {
          // Track logout click
          trackConfirmationAlertClick('logout', true);

          const cookies = Object.keys(Cookies.get());
          cookies.forEach(cookie => {
            Cookies.remove(cookie);
          });

          sessionStorage.clear();
          clearWithTheme();

          await supabaseClient.auth.signOut();

          navigate("/about");
          setIsLoading(false);
        },
      },
      cancel: {
        label: "No",
        onClick: () => {
          // Track confirmation alert action - user clicked No
          trackConfirmationAlertClick('logout', false);
          setIsLoading(false);
        },
      },
    });
  };

  return (
    <>
      <section
        className="d-flex justify-content-center align-items-center"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: isMobile ? "100%" : "calc(100% - 32px)",
          height: 72,
          zIndex: 100,
          background: background,
          padding: isMobile ? "0 24px" : "0 80px",
          marginLeft: isMobile ? "0" : "32px",
          boxSizing: "border-box"
        }}
      >
        <nav
          className="w-100 d-flex justify-content-between align-items-center"
          style={{ zIndex: 101, position: "absolute" }}
        >
          <div className="d-flex align-items-center" style={{ marginLeft: isMobile ? "20px" : "0" }}>
            <img
              src={logo}
              alt="Logo"
              width={60}
              height={20.3}
              className="cursor-pointer"
              onClick={() => navigate("/")}
            />
          </div>
          <div className="d-flex align-items-center justify-content-end gap-4">
            <ul className="d-flex" style={{ gap: 32, marginRight: "20px" }}>
              {isChatPage ? (
                // Chat page: Show avatar with initials if logged in; no CTA when logged out
                user ? (
                  // User is logged in - show avatar and menu as before
                  !isMobile ? (
                    navigationLinks.map((item, index) =>
                      item.action ? (
                        <li key={index} className="d-flex align-items-center">
                          <button
                            className="nav-item-brad"
                            style={{
                              color: theme,
                              fontWeight: 600,
                            }}
                            onClick={item.action}
                            aria-label={`Navigate to ${item.title}`}
                          >
                            {item.title}
                          </button>
                        </li>
                      ) : (
                        <li key={index} className="d-flex align-items-center">
                          {item.title}
                        </li>
                      )
                    )
                  ) : (
                    <li>
                      <div className="d-flex align-items-center gap-2">
                        <UserHeader onClick={() => logout()} />
                       {openMenuMobile ? (
                        <div 
                          onClick={() => setOpenMenuMobile(false)}
                          style={{ width: 22, height: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                        >
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13 1L1 13" stroke={themeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M1 1L13 13" stroke={themeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      ) : (
                        <IconMenu2
                          onClick={() => setOpenMenuMobile(true)}
                          style={{ width: 22, height: 22, cursor: "pointer", color: themeColor }}
                        />
                      )}
                      </div>
                    </li>
                  )
                ) : (
                  renderGuestAuthButtons()
                )
              ) : !isMobile ? (
                // Other pages: Show Chat button + normal navigation for logged in users
                [
                  // Add Chat button for non-chat pages
                  ...(isChatPage ? [] : [{
                    id: "chat",
                    title: "Chat",
                    action: () => navigate("/chat"),
                  }]),
                  ...navigationLinks
                ].map((item, index) =>
                  item.action ? (
                    <li key={index} className="d-flex align-items-center">
                      <button
                        className="nav-item-brad"
                        style={{
                          color: theme,
                          fontWeight: 600,
                          fontSize: 16,
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: "6px 2px",
                        }}
                        onClick={item.action}
                        aria-label={`Navigate to ${item.title}`}
                      >
                        {item.title}
                      </button>
                    </li>
                  ) : (
                    <li key={index} className="d-flex align-items-center">
                      {item.title}
                    </li>
                  )
                )
              ) : (
                // Mobile: Show Chat button (if not on chat page) + UserHeader with menu
                <>
                  {!isChatPage && (
                    <li className="d-flex align-items-center">
                      <button
                        className="nav-item-brad"
                        style={{
                          color: theme,
                          fontWeight: 600,
                          fontSize: 14,
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: "4px 8px",
                          marginRight: "8px",
                        }}
                        onClick={() => navigate("/chat")}
                        aria-label="Navigate to Chat"
                      >
                        Chat
                      </button>
                    </li>
                  )}
                  <li>
                    <div className="d-flex align-items-center gap-2">
                      <UserHeader onClick={() => logout()} />
                     {openMenuMobile ? (
                      <div 
                        onClick={() => setOpenMenuMobile(false)}
                        style={{ width: 22, height: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M13 1L1 13" stroke={themeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M1 1L13 13" stroke={themeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    ) : (
                      <IconMenu2
                        onClick={() => setOpenMenuMobile(true)}
                        style={{ width: 22, height: 22, cursor: "pointer", color: themeColor }}
                      />
                    )}
                    </div>
                  </li>
                </>
              )}
            </ul>
          </div>
        </nav>
      </section>
      {openMenuMobile && <MenuMobile setOpenMenuMobile={setOpenMenuMobile} />}
    </>
  );
};

export default HeaderPageBrad;
